import type Phaser from 'phaser';
import type { InputFrame, Vec2 } from '../core/types';
import type { TouchControls } from './touch';
import { consumeTouchSpell } from './touchSpells';

export interface KeyTracker { move(): Vec2; isDown(code: string): boolean; release(codes: string[]): void; clear(): void; dispose(): void; }

export function createKeyTracker(target: EventTarget): KeyTracker {
  const down = new Set<string>();
  // Touches relâchées de force : l'auto-répétition du clavier ne les ré-arme pas avant un vrai keyup.
  const blocked = new Set<string>();
  const onDown = (e: Event): void => { const code = (e as KeyboardEvent).code; if (!blocked.has(code)) down.add(code); };
  const onUp = (e: Event): void => { const code = (e as KeyboardEvent).code; down.delete(code); blocked.delete(code); };
  const onBlur = (): void => { down.clear(); blocked.clear(); };
  target.addEventListener('keydown', onDown);
  target.addEventListener('keyup', onUp);
  target.addEventListener('blur', onBlur);
  return {
    move: () => ({ x: (down.has('KeyD') ? 1 : 0) - (down.has('KeyA') ? 1 : 0), y: (down.has('KeyS') ? 1 : 0) - (down.has('KeyW') ? 1 : 0) }),
    isDown: (code) => down.has(code),
    release: (codes) => { for (const c of codes) { if (down.delete(c)) blocked.add(c); } },
    clear: () => { down.clear(); blocked.clear(); },
    dispose() {
      target.removeEventListener('keydown', onDown);
      target.removeEventListener('keyup', onUp);
      target.removeEventListener('blur', onBlur);
      down.clear();
      blocked.clear();
    },
  };
}

export interface InputSource { frame(): InputFrame; dispose(): void; }

/**
 * Source d'entrée de la salle. Ordinateur : souris + ZQSD. Mobile (touch actif) : joystick virtuel pour se déplacer,
 * Autohit : tir maintenu tant qu'un monstre vivant est à portée et `aim: null` (le coeur vise tout seul), moitié droite = esquive,
 * appui sur une case de la barre de sorts = sort de cette case.
 */
export function createInputSource(scene: Phaser.Scene, keys: KeyTracker, touch?: TouchControls, hasTarget: () => boolean = () => false): InputSource {
  // Tant que la souris n'a pas bougé, aim reste null : le coeur vise alors le monstre le plus proche (ou la direction du regard).
  let moved = false;
  const onMove = (): void => { moved = true; };
  scene.input.on('pointermove', onMove);
  return {
    frame() {
      if (touch?.enabled()) {
        const t = touch.move();
        const k = keys.move();
        const joy = t.x !== 0 || t.y !== 0;
        const sp = (i: number): boolean => consumeTouchSpell(i) || keys.isDown(`Digit${i + 1}`);
        return { move: joy ? t : k, aim: null, primary: hasTarget(), spells: [sp(0), sp(1), sp(2), sp(3)], dodge: touch.consumeDodge() || keys.isDown('Space'), interact: false };
      }
      const ptr = scene.input.activePointer;
      // recalculé à chaque pas : la caméra suit le joueur, worldX/worldY ne bougent qu'avec la souris
      const aim = moved ? scene.cameras.main.getWorldPoint(ptr.x, ptr.y) : null;
      return { move: keys.move(), aim: aim ? { x: aim.x, y: aim.y } : null, primary: ptr.leftButtonDown(), spells: [keys.isDown('Digit1'), keys.isDown('Digit2'), keys.isDown('Digit3'), keys.isDown('Digit4')], dodge: keys.isDown('Space'), interact: ptr.rightButtonDown() };
    },
    dispose() { scene.input.off('pointermove', onMove); },
  };
}
